import { get, toPath, uniq } from 'lodash';
import { FileWithPreview } from 'react-dropzone';

import { ErrorItem } from '../../components/ErrorsPresenter/ErrorsPresenter';
import { BannerId, SiteLinkData } from '../../typings/banners';
import {
    BannerSiteLinksEditorData,
    EditBannersState,
    EditedBanner,
    EditedBannerTypeText,
    getEditedBannerWithOrganization,
    getTextBannersWithContacts
} from '../../typings/bannersEditState';
import {
    ValidationErrorData,
    ValidationErrorsSet,
    ValidationResultItem
} from '../../typings/common';
import { AdvType, AdvTypeClient } from '../../typings/domain';
import { State } from '../../typings/state';
import { SitelinksValidator } from '../BannerEditValidation/SitelinksValidator';
import { FieldPathMap } from '../ErrorsMapper/ErrorsMapper';

import { BannerPriceErrorMapper } from './BannerPriceErrorMapper/BannerPriceErrorMapper';
import { bannersTextErrorMapper } from './ErrorsTextMapper/ErrorsTextMapper';

// Функция сортировки ошибок по serverPath для правильного маппинга на клиентские ошибки
const sortFieldPathMaps = function(list: FieldPathMap[]): FieldPathMap[] {
    return list.sort(
        (a, b) => toPath(b.serverPath).length - toPath(a.serverPath).length
    );
};

export const bannerFieldErrorMapper = function(index: string): FieldPathMap[] {
    return sortFieldPathMaps([
        {
            serverPath: `adUpdateItems`,
            clientPath: `banners`
        },
        {
            serverPath: `adUpdateItems.${index}`,
            clientPath: `banners.${index}`
        },
        {
            serverPath: `adUpdateItems.${index}.title`,
            clientPath: `banners.${index}.title`
        },
        {
            serverPath: `adUpdateItems.${index}.titleExtension`,
            clientPath: `banners.${index}.titleExtension`
        },
        {
            serverPath: `adUpdateItems.${index}.body`,
            clientPath: `banners.${index}.body`
        },
        {
            serverPath: `adUpdateItems.${index}.domain`,
            clientPath: `banners.${index}.domain`
        },
        {
            serverPath: `adUpdateItems.${index}.href`,
            clientPath: `banners.${index}.href`
        },
        {
            serverPath: `adUpdateItems.${index}.turboGalleryHref`,
            clientPath: `banners.${index}.turboGalleryHref`
        },
        {
            serverPath: `adUpdateItems.${index}.displayHref`,
            clientPath: `banners.${index}.displayHref`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet`,
            clientPath: `banners.${index}.sitelinks`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet.sitelinks.${index}`,
            clientPath: `banners.${index}.sitelinks`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet.sitelinks.${index}.title`,
            clientPath: `banners.${index}.sitelinks.${index}.title`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet.sitelinks.${index}.description`,
            clientPath: `banners.${index}.sitelinks.${index}.descr`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet.sitelinks.${index}.href`,
            clientPath: `banners.${index}.sitelinks.${index}.link`
        },
        {
            serverPath: `adUpdateItems.${index}.sitelinkSet.sitelinks.${index}.turbo`,
            clientPath: `banners.${index}.sitelinks.${index}.turbo`
        },
        {
            serverPath: `adUpdateItems.${index}.calloutIds`,
            clientPath: `banners.${index}.callouts`
        },
        {
            serverPath: `adUpdateItems.${index}.image`,
            clientPath: `banners.${index}.image`
        },
        {
            serverPath: `adUpdateItems.${index}.turboLandingParams.hrefParams`,
            clientPath: `banners.${index}.turboHrefParams`
        },
        {
            serverPath: `adUpdateItems.${index}.pixels.${index}`,
            clientPath: `banners.${index}.pixels.${index}`
        },
        {
            serverPath: `adUpdateItems.${index}.pixels`,
            clientPath: `banners.${index}.pixels`
        },
        {
            serverPath: `adUpdateItems.${index}.measurers.${index}`,
            clientPath: `banners.${index}.measurers.${index}`
        },
        {
            serverPath: `adUpdateItems.${index}.tnsId`,
            clientPath: `banners.${index}.tnsId`
        },
        {
            serverPath: `adAddItems`,
            clientPath: `banners`
        },
        {
            serverPath: `adAddItems.${index}`,
            clientPath: `banners.${index}`
        },
        {
            serverPath: `adAddItems.${index}.title`,
            clientPath: `banners.${index}.title`
        },
        {
            serverPath: `adAddItems.${index}.titleExtension`,
            clientPath: `banners.${index}.titleExtension`
        },
        {
            serverPath: `adAddItems.${index}.body`,
            clientPath: `banners.${index}.body`
        },
        {
            serverPath: `adAddItems.${index}.domain`,
            clientPath: `banners.${index}.domain`
        },
        {
            serverPath: `adAddItems.${index}.href`,
            clientPath: `banners.${index}.href`
        },
        {
            serverPath: `adAddItems.${index}.turboGalleryHref`,
            clientPath: `banners.${index}.turboGalleryHref`
        },
        {
            serverPath: `adAddItems.${index}.sitelinkSet`,
            clientPath: `banners.${index}.sitelinks`
        },
        {
            serverPath: `adAddItems.${index}.calloutIds`,
            clientPath: `banners.${index}.callouts`
        },
        {
            serverPath: `adAddItems.${index}.image`,
            clientPath: `banners.${index}.image`
        },
        {
            serverPath: `adAddItems.${index}.turboLandingParams.hrefParams`,
            clientPath: `banners.${index}.turboHrefParams`
        },
        {
            serverPath: `adAddItems.${index}.creativeId`,
            clientPath: `banners.${index}.creativeId`
        },
        {
            serverPath: `adUpdateItems.${index}.creativeId`,
            clientPath: `banners.${index}.creativeId`
        },
        {
            serverPath: `adAddItems.${index}.displayHref`,
            clientPath: `banners.${index}.displayHref`
        },
        {
            serverPath: `adAddItems.${index}.pixels.${index}`,
            clientPath: `banners.${index}.pixels.${index}`
        },
        {
            serverPath: `adAddItems.${index}.pixels`,
            clientPath: `banners.${index}.pixels`
        },
        {
            serverPath: `adUpdateItems.${index}.turboLandingId`,
            clientPath: `banners.${index}.turboLandingId`
        },
        {
            serverPath: `adAddItems.${index}.turboLandingId`,
            clientPath: `banners.${index}.turboLandingId`
        },
        {
            serverPath: `adAddItems.${index}.measurers.${index}`,
            clientPath: `banners.${index}.measurers.${index}`
        },
        {
            serverPath: `adAddItems.${index}.tnsId`,
            clientPath: `banners.${index}.tnsId`
        },
        {
            serverPath: `adUpdateItems.${index}.organization`,
            clientPath: `banners.${index}.organization`
        }
    ]);
};

export const internalBannerFieldErrorMapper = function(
    index: string
): FieldPathMap[] {
    return sortFieldPathMaps([
        {
            serverPath: `adAddItems.${index}.templateId`,
            clientPath: `banners.${index}.templateId`
        },
        {
            serverPath: `adAddItems.${index}.description`,
            clientPath: `banners.${index}.description`
        },
        {
            serverPath: `adAddItems.${index}.templateVars.${index}`,
            clientPath: `banners.${index}.templateVars.${index}`
        },

        {
            serverPath: `adUpdateItems.${index}.description`,
            clientPath: `banners.${index}.description`
        },
        {
            serverPath: `adUpdateItems.${index}.templateVars.${index}`,
            clientPath: `banners.${index}.templateVars.${index}`
        }
    ]);
};

export const sitelinksFieldErrorMapper = function(
    index: string
): FieldPathMap[] {
    return sortFieldPathMaps([
        {
            serverPath: `sitelinkSetsAddItems.${index}`,
            clientPath: `set.${index}.sitelinks`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks`,
            clientPath: `set.${index}.sitelinks`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks.${index}`,
            clientPath: `set.${index}.sitelinks`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks.${index}.title`,
            clientPath: `set.${index}.sitelinks.${index}.title`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks.${index}.description`,
            clientPath: `set.${index}.sitelinks.${index}.descr`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks.${index}.href`,
            clientPath: `set.${index}.sitelinks.${index}.link`
        },
        {
            serverPath: `sitelinkSetsAddItems.${index}.sitelinks.${index}.turbo`,
            clientPath: `set.${index}.sitelinks.${index}.turbo`
        }
    ]);
};

export const mapUploadImagesErrorsToNames = (
    uploadedFiles: FileWithPreview[],
    errors: ValidationResultItem[]
): Record<string, ValidationResultItem[]> => {
    return errors.reduce((result, error) => {
        const index = Number(toPath(error.path)[0]);
        const name = uploadedFiles[index].name;

        result[name] = [
            errors.filter(newError => newError.value === index.toString())[0]
        ];

        return result;
    }, {});
};

export const getBannerPriceErrorsIdx = (
    errors: ValidationResultItem[]
): string[] => {
    return uniq(
        errors.map(error => {
            return toPath(error.path)[0];
        })
    );
};

export const mapBannerPriceErrors = (
    errors: ValidationResultItem[]
): ErrorItem[] => {
    return (errors || []).map((error: ValidationResultItem) => {
        return {
            path: error.path,
            text: BannerPriceErrorMapper({ ...error })
        };
    });
};

export function getFormattedErrors(
    validationErrors: ValidationResultItem[],
    idxIndex: number
): ValidationErrorsSet {
    return validationErrors.reduce(
        (acc: ValidationErrorsSet, next: ValidationResultItem) => {
            const validationResult = next;
            const path = validationResult.path;
            const idx = toPath(path)[idxIndex];

            if (!acc[idx]) {
                acc[idx] = [];
            }

            acc[idx].push(validationResult);

            return acc;
        },
        {} as ValidationErrorsSet
    );
}

export function getBannersErrorsSetWithIds(
    bannersIds: string[],
    errors: ValidationResultItem[]
): ValidationErrorsSet {
    return bannersIds.reduce(
        (acc: ValidationErrorsSet, next: BannerId) => {
            acc[next] = errors;

            return acc;
        },
        {} as ValidationErrorsSet
    );
}

export function getClientsContactsErrors(
    bannerType: AdvType | AdvTypeClient
): ValidationResultItem[] {
    const errorTextSet = {
        code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_VCARD_ID',
        path: 'banners[0]',
        params: null
    };
    const errorImageOrVideoSet = {
        code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_TURBOLANDING_ID',
        path: 'banners[0]',
        params: null
    };
    const errorMcBannerSet = {
        code: 'BannerDefectIds.Gen.REQUIRED_HREF',
        path: 'banners[0]',
        params: null
    };

    if (
        bannerType === AdvType.TEXT ||
        bannerType === AdvTypeClient.TEXT_MOBILE
    ) {
        const textBannerMessage = bannersTextErrorMapper(errorTextSet);

        return [
            {
                ...errorTextSet,
                text: textBannerMessage
            }
        ];
    }

    if (bannerType === AdvType.CPC_VIDEO || bannerType === AdvType.IMAGE_AD) {
        const imageOrVideoMessage = bannersTextErrorMapper(
            errorImageOrVideoSet
        );

        return [
            {
                ...errorImageOrVideoSet,
                text: imageOrVideoMessage
            }
        ];
    }

    if (bannerType === AdvType.MCBANNER) {
        const mcBannerMessage = bannersTextErrorMapper(errorMcBannerSet);

        return [
            {
                ...errorMcBannerSet,
                text: mcBannerMessage
            }
        ];
    }

    return [];
}

export function validateContacts(
    banners: Record<string, EditedBanner>
): ValidationErrorsSet {
    return Object.keys(banners).reduce(
        (acc: ValidationErrorsSet, val: string) => {
            const banner = banners[val];
            const bannerWithContacts = getTextBannersWithContacts(banner);
            const isVCardEmpty =
                banner.bannerType === AdvType.TEXT ||
                banner.bannerType === AdvTypeClient.TEXT_MOBILE
                    ? !Boolean(banner.vCardId)
                    : true;
            const bannerWithOrganization = getEditedBannerWithOrganization(
                banner
            );

            if (bannerWithContacts) {
                if (
                    !bannerWithContacts.contacts.href &&
                    !(
                        bannerWithContacts.contacts.turbolanding &&
                        bannerWithContacts.contacts.turbolanding.page
                    ) &&
                    isVCardEmpty &&
                    !(
                        bannerWithOrganization &&
                        bannerWithOrganization.permalinkId
                    )
                ) {
                    acc[val] = getClientsContactsErrors(banner.bannerType);
                }
            }

            return acc;
        },
        {}
    );
}

export function validateSiteLinks(
    banners: Array<EditedBannerTypeText>,
    siteLinksData: Array<BannerSiteLinksEditorData>,
    siteLinksBannersIds: Array<BannerId>,
    state: EditBannersState
): ValidationResultItem[][] {
    return banners.reduce((acc, banner, id) => {
        if (!siteLinksBannersIds.includes(banner.id)) {
            return acc;
        }

        const siteLinksItems = siteLinksData[id].items.map(
            (sitelink: SiteLinkData) => {
                return {
                    ...sitelink,
                    link:
                        sitelink.link.length > 0
                            ? sitelink.protocol + sitelink.link
                            : ''
                };
            }
        );

        return [
            ...acc,
            SitelinksValidator.validate(
                `sitelinks`,
                {
                    ...banner,
                    sitelinks: {
                        ...banner.sitelinks,
                        items: siteLinksItems
                    }
                },
                state
            )
        ];
    }, []);
}

const DEFAULT_ERRORS: ValidationErrorData[] = [];

export const getSummaryBannerErrors = (
    state: State,
    bannerId: BannerId
): ValidationErrorData[] => {
    // Тут внимание!
    // Одновременно внутренних типов кампаний и внешних быть не может,
    // к тому же идентификаторы кампаний внезависимости от их типа уникальны.
    // Поэтому тут допустим такой доступ к баннеру по его идентификатору.
    const banner =
        state.editBanners.banners[bannerId] ||
        state.editInternalBanners.banners[bannerId];

    let errors = [
        ...get(banner, 'errors.errors', DEFAULT_ERRORS),
        ...get(banner, 'errors.organization.errors', DEFAULT_ERRORS)
    ];

    if (banner.bannerType === AdvType.CPM_GEOPRODUCT) {
        errors = [
            ...errors,
            ...get(banner, 'errors.turboLandingId.errors', DEFAULT_ERRORS)
        ];
    }

    return errors;
};
